/****************************************************
 * Module Name:  nivodaUserMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import NivodaUserProxy from "../../utility/proxy/nivodautility/nivodaUserProxy";
import AddNivodaUserModal from "./addNivodaUserModal";
import PageTitle from "../common/pageTitle";

export class NivodaUserMaster extends Component {
    constructor(props) {
        super(props);

        this.state = {

            nivodausers: [],
            showAddNivodaUserModal: false,
            accesDenied: false,
            editOperation: false,
            sourceData: {},
            errorSnackbar: false,
            successSnackbar: false,
            snackbarMessage: "",
            loadingButton: false,
            showNivodaUserLoadSpinner: true,
            local_Permission: Constants.USER_PERMISSION.filter(
                (iPermission) =>
                    iPermission.permissionid === Constants.PERMISSIONS.ANALYSIS_NIVODA_USER
            ),
        };

        this.checkPermission()
    }

    componentDidMount() {
        PageTitle("ANALYSIS_NIVODA_USER");
    }

    checkPermission = () => {
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            this.setState({
                accesDenied: true
            })
        } else {
            this.fetchNivodaUserData(false);
        }
    }


    fetchNivodaUserData = (bAnimateSpinner) => {
        bAnimateSpinner && this.setState({ showNivodaUserLoadSpinner: true });
        NivodaUserProxy.GetAllNivodaUser((responseData) => {
            if (responseData) {
                this.setState({ nivodausers: responseData, showNivodaUserLoadSpinner: false })
            } else {
                this.setState({ errorSnackbar: true, showNivodaUserLoadSpinner: false })
            }
        });
    };

    columns = [
        {
            field: "user",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            width: 610,
            renderCell: renderCellExpand,
        },
        {
            field: "username",
            headerName: "USERNAME",
            headerAlign: "center",
            align: "center",
            width: 415,
            renderCell: renderCellExpand,
        },
        {
            field: "password",
            headerName: "PASSWORD",
            headerAlign: "center",
            align: "center",
            width: 405,
            renderCell: (params) => atob(params.value),
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            width: 365,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            this.setState({
                                showAddNivodaUserModal: true,
                                editOperation: true,
                                sourceData: params.row,
                                loadingButton: false,
                            });
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    handleValidation = (nivodaUserData) => {
        //TODO do validation if needed here
        return true;
    };

    addNivodaUser = (nivodaUserData) => {
        this.setState({ loadingButton: true });
        if (this.state.editOperation) {
            //Updating Nivoda User, and once nivodauser is updated on call refresh the list of current NivodaUsers
            NivodaUserProxy.UpdateNivodaUser(nivodaUserData, (response) => {
                if (response) {
                    this.fetchNivodaUserData(true);
                    this.setState({ loadingButton: false, showAddNivodaUserModal: false, successSnackbar: true, snackbarMessage: "Nivode user updated successfully." });
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        } else {
            //Creating new Nivoda User, and once Nivoda User is created on call refresh the list of current nivodausers
            NivodaUserProxy.AddNivodaUser(nivodaUserData, (response) => {
                if (response) {
                    this.fetchNivodaUserData(true);
                    this.setState({ loadingButton: false, showAddNivodaUserModal: false, successSnackbar: true, snackbarMessage: "Nivoda user added successfully." });
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        }
    };

    render() {
        const CustomToolbar = () => {
            return (
                <GridToolbarContainer
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        marginHorizontal: "5px",
                        "&.MuiDataGrid-toolbarContainer": {
                            padding: "0px 4px !important",
                        },
                    }}
                >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarExport />
                    <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                    {this.state.showNivodaUserLoadSpinner ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                color="primary"
                                fontSize="medium"
                                onClick={() => this.fetchNivodaUserData(true)}
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                    {Utility.isAddAllowed(this.state.local_Permission) ? (
                        <Tooltip title="Add Nivoda User">
                            <Button
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{ ml: "8px" }}
                                onClick={() =>
                                    this.setState({
                                        showAddNivodaUserModal: true,
                                        editOperation: false,
                                        sourceData: {},
                                    })
                                }
                                startIcon={<AddBoxOutlinedIcon size="large" />}
                            >
                                Nivoda User
                            </Button>
                        </Tooltip>
                    ) : null}
                    <GridToolbarQuickFilter debounceMs={500} />
                </GridToolbarContainer>
            );
        }
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            return <NoPermission />
        }
        const rows = this.state.nivodausers;
        const columns = Utility.isUpdateAllowed(this.state.local_Permission)
            ? this.columns
            : this.columns.filter((col) => col.field !== "edit");

        return (
            <Box>
                <CommonSnackbar
                    show={this.state.errorSnackbar}
                    message={<Box>{Constants.APiFailErrorMessage}</Box>}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    handleClose={() => this.setState({ errorSnackbar: false })}
                    title="Failed"
                />

                <CommonSnackbar
                    show={this.state.successSnackbar}
                    message={this.state.snackbarMessage}
                    variant={"success"}
                    autoHide={Constants.SnackBarCloseTimer}
                    handleClose={() => this.setState({ successSnackbar: false })}
                    title="Success"
                />

                <br />

                {this.state.showAddNivodaUserModal ? (
                    <AddNivodaUserModal
                        Show={true}
                        handleClose={() => this.setState({ showAddNivodaUserModal: false })}
                        handleSave={this.addNivodaUser}
                        handleValidation={this.handleValidation}
                        editOperation={this.state.editOperation}
                        sourceData={this.state.sourceData}
                        loading={this.state.loadingButton}
                    />
                ) : (
                    <Box />
                )}
                <Box px={1}>
                    <Box
                        sx={{
                            width: "100%",
                            backgroundColor: '#fff',
                            color: '#000',
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "83vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            getRowHeight={() => "auto"}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },
                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: CustomToolbar }}
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            loading={this.state.showNivodaUserLoadSpinner}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                        id: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default NivodaUserMaster;
